import ExpandableText from '@/components/ExpandableText';
import PriceDisplay from '@/components/PriceDisplay';
import PromoCode from '@/components/PromoCode';
import Changeability from '@/components/purchase/card/Changeability';
import Refundability from '@/components/purchase/card/Refundability';
import { TransText } from '@/i18n/trans/text';
import { type AppliedReductionDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import _kebabCase from 'lodash/kebabCase';
import type { FC, ReactNode } from 'react';

interface BaseOfferCardProps {
  title: string;
  amount: number;
  isSelected: boolean;
  onSelect: () => void;
  description?: string;
  isRefundable?: boolean;
  isChangeable?: boolean;
  showPassengerInfo?: boolean;
  appliedReduction?: AppliedReductionDTO;
  titleAdornment?: ReactNode;
  ['data-testid']: string;
}

const BaseOfferCard: FC<BaseOfferCardProps> = ({
  title,
  amount,
  isSelected,
  onSelect,
  description,
  isRefundable = false,
  isChangeable = false,
  showPassengerInfo = false,
  appliedReduction,
  titleAdornment,
  ...props
}) => {
  return (
    <div
      className={cn(
        'flex w-full flex-col justify-between overflow-hidden rounded-lg border border-primary hover:cursor-pointer hover:bg-primary-pale',
        { 'border-2 border-primary-dark bg-primary-light': isSelected }
      )}
      onClick={onSelect}
      data-testid="offer-item"
    >
      <div
        className={cn(
          'flex flex-grow flex-col gap-1 border-b border-primary px-4 py-3 laptop:gap-0',
          {
            'border-primary-dark': isSelected,
          }
        )}
      >
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-1">
            <Typography variant="body1-bold">
              <span>{title}</span>
            </Typography>
            {titleAdornment}
          </div>
          <Typography variant="body1-bold" className="text-primary">
            <PriceDisplay amount={amount} />
          </Typography>
        </div>
        {appliedReduction?.code && (
          <PromoCode
            className="mt-2"
            code={appliedReduction.code}
            description={appliedReduction.description}
          />
        )}
        <Divider className="my-3" variant={isSelected ? 'medium' : 'regular'} />
        <ExpandableText text={description} />
        <div className="flex flex-col">
          <div className="flex flex-row gap-1">
            <Refundability condition={isRefundable} />
            <Changeability condition={isChangeable} />
          </div>
          {showPassengerInfo && (
            <div className="mt-2 flex flex-row gap-1">
              <Icons.users className="text-primary" />
              <Typography variant="body2">
                <TransText i18nKey="priceIsForAllPassengers" />
              </Typography>
            </div>
          )}
        </div>
      </div>
      <Button
        variant={isSelected ? 'primary' : 'secondary'}
        onPress={onSelect}
        data-testid={_kebabCase(`${props['data-testid']}-${title}-button}`)}
        className={cn('w-full rounded-none border-0')}
        startAdornment={
          isSelected && (
            <Icons.checkCircle width={20} height={20} className="self-center" />
          )
        }
      >
        <Typography variant="body1-bold">
          <TransText i18nKey={isSelected ? 'selected' : 'select'} />
        </Typography>
      </Button>
    </div>
  );
};

export default BaseOfferCard;
